import React, { useState } from 'react';
import { useIntl } from 'umi';
import { Button, Form, Input, Tooltip, message } from 'antd';
import { CheckOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';

import { F, INTL_ACTION, INTL_ENTITY, getIntlTranslation } from '@/utils/intl';
import { getAPIErrorMessage, processAPIResponseNow } from '@/utils/request';
import { createSkillRequest, updateSkillRequest } from '@/services/api/skill';
import type { SkillInput, SkillModel } from '@/typings/models/Skill';
import { isSlugError, normalizeSlugInput } from './utils';
import styles from './index.module.less';

type SkillFormProps = {
  /** Edits this skill; creates a new one when omitted. */
  skill?: SkillModel;
  onSaved: () => void;
  onCancel?: () => void;
};

const SkillForm: React.FC<SkillFormProps> = ({ skill, onSaved, onCancel }) => {
  const intl = useIntl();
  const [form] = Form.useForm<SkillInput>();
  const [saving, setSaving] = useState(false);
  const label = Form.useWatch('label', form);
  const slug = Form.useWatch('slug', form);
  const isEdit = !!skill;
  // A new skill may leave the slug empty (the API derives it); an existing one keeps it.
  const canSubmit = !!label?.trim() && (!isEdit || !!slug);

  const labelText = intl.formatMessage({ id: 'common.attribute.label', defaultMessage: 'Label' });
  const slugText = intl.formatMessage({ id: 'common.attribute.slug', defaultMessage: 'Slug' });

  const handleFinish = async (values: SkillInput) => {
    setSaving(true);
    const data: SkillInput = { label: values.label.trim(), ...(values.slug ? { slug: values.slug } : {}) };
    const response = isEdit ? await updateSkillRequest(skill.id, data) : await createSkillRequest(data);
    processAPIResponseNow(response, {
      successMessage: getIntlTranslation(intl, {
        entity: INTL_ENTITY.SKILL,
        action: isEdit ? INTL_ACTION.EDIT_SUCCESS : INTL_ACTION.CREATE_SUCCESS,
      }),
      onEnd: () => setSaving(false),
      onSuccess: () => {
        if (!isEdit) form.resetFields();
        onSaved();
      },
      onError: (error) => {
        const errorMessage = getAPIErrorMessage(error);
        // Taken or malformed slugs belong next to the slug input, not in a toast.
        if (isSlugError(errorMessage)) form.setFields([{ name: 'slug', errors: [errorMessage] }]);
        else message.error(errorMessage);
      },
    });
  };

  // A server-side slug error has no rule to re-run, so drop it once the slug is edited.
  const handleValuesChange = (changed: Partial<SkillInput>) => {
    if ('slug' in changed && form.getFieldError('slug').length) form.setFields([{ name: 'slug', errors: [] }]);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape' || !onCancel) return;
    // Cancels the edit only — without this the drawer would close too.
    event.stopPropagation();
    onCancel();
  };

  return (
    <Form
      form={form}
      initialValues={skill}
      onFinish={handleFinish}
      onValuesChange={handleValuesChange}
      onKeyDown={handleKeyDown}
      className={styles.form}
    >
      <Form.Item name="label" className={styles.labelField}>
        <Input placeholder={labelText} aria-label={labelText} autoFocus={isEdit} maxLength={255} />
      </Form.Item>
      <Form.Item name="slug" normalize={normalizeSlugInput} className={styles.slugField}>
        <Input placeholder={slugText} aria-label={slugText} className={styles.slugInput} maxLength={100} />
      </Form.Item>
      {isEdit ? (
        <div className={styles.formActions}>
          <Tooltip title={<F id="common.actions.save" defaultMessage="Save" />}>
            <Button
              type="primary"
              htmlType="submit"
              icon={<CheckOutlined />}
              loading={saving}
              disabled={!canSubmit}
              aria-label={intl.formatMessage({ id: 'common.actions.save', defaultMessage: 'Save' })}
            />
          </Tooltip>
          <Tooltip title={<F id="common.actions.cancel" defaultMessage="Cancel" />}>
            <Button
              icon={<CloseOutlined />}
              onClick={onCancel}
              aria-label={intl.formatMessage({ id: 'common.actions.cancel', defaultMessage: 'Cancel' })}
            />
          </Tooltip>
        </div>
      ) : (
        <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={saving} disabled={!canSubmit}>
          <F id="common.actions.add" defaultMessage="Add" />
        </Button>
      )}
    </Form>
  );
};

export default SkillForm;
